import { useEffect, useRef, useState } from "react";
import AccountButton from "../AccountButton";
import AccountSection from "../AccountSection";
import {
  addMedicalCondition,
  addMedicalDevice,
  addTherapy,
  deleteMedicalCondition,
  deleteMedicalDevice,
  deleteTherapy,
  getMedicalConditions,
  getMedicalDevices,
  getTherapies,
  updateMedicalCondition,
  updateMedicalDevice,
  updateTherapy,
} from "../api/apiFunctions";
import { DisplayOnCardPill } from "../components/Pills";
import { t } from "../i18n";
import { replaceNullWithPlaceholder } from "../utils";
import { getAuthToken } from "../utils/auth";

export default function ConditionsPage({ lang = "en", goToTab }) {
  const [search, setSearch] = useState("");
  // const [form, setForm] = useState({
  //   condition: "",
  //   alias: "",
  //   moreInfo: "",
  //   details: "",
  // });
  const [conditions, setConditions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingTherapy, setIsSavingTherapy] = useState(false);
  const [isSavingDevice, setIsSavingDevice] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [conditionToDelete, setConditionToDelete] = useState(null);
  const [deletingTherapyIndex, setDeletingTherapyIndex] = useState(null);
  const [showTherapyDeleteConfirm, setShowTherapyDeleteConfirm] =
    useState(false);
  const [therapyToDelete, setTherapyToDelete] = useState(null);
  const [deletingDeviceIndex, setDeletingDeviceIndex] = useState(null);
  const [showDeviceDeleteConfirm, setShowDeviceDeleteConfirm] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [tempCondition, setTempCondition] = useState({
    name: "",
    details: "",
  });
  const [tempTherapy, setTempTherapy] = useState("");
  const [tempDevice, setTempDevice] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingTherapyIndex, setEditingTherapyIndex] = useState(null);
  const [editingDeviceIndex, setEditingDeviceIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    alias: "",
    details: "",
    status: "active",
  });
  const [editTherapyForm, setEditTherapyForm] = useState({
    name: "",
    description: "",
    frequency: "",
    duration: "",
    status: "active",
  });
  const [editDeviceForm, setEditDeviceForm] = useState({
    name: "",
    type: "",
    brand: "",
    model: "",
    description: "",
    status: "active",
  });
  const [therapies, setTherapies] = useState([]);
  const [medicalDevices, setMedicalDevices] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  // Medical conditions API search states
  const [conditionSearch, setConditionSearch] = useState("");
  const [conditionResults, setConditionResults] = useState([]);
  const [showConditionDropdown, setShowConditionDropdown] = useState(false);
  const [isConditionLoading, setIsConditionLoading] = useState(false);
  const conditionDropdownRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    loadMedicalConditions();
    loadTherapies();
    loadMedicalDevices();
  }, []);

  // Load medical conditions from API
  const loadMedicalConditions = async () => {
    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token found");
      setError("Please log in to access your medical conditions.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      let response;
      try {
        response = await getMedicalConditions(token);
      } catch (apiError) {
        console.error("API call failed:", apiError);
        throw apiError;
      }

      // Transform API response to match local state structure
      let conditionsData = [];

      if (
        response &&
        response.data &&
        response.data.medicalConditions &&
        Array.isArray(response.data.medicalConditions)
      ) {
        // Response has data.medicalConditions property with array
        conditionsData = response.data.medicalConditions;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Response has data property with array
        conditionsData = response.data;
      } else if (
        response &&
        response.conditions &&
        Array.isArray(response.conditions)
      ) {
        // Response has conditions property
        conditionsData = response.conditions;
      } else if (
        response &&
        response.medicalConditions &&
        Array.isArray(response.medicalConditions)
      ) {
        // Response has medicalConditions property
        conditionsData = response.medicalConditions;
      } else if (response && Array.isArray(response)) {
        // Response is directly an array
        conditionsData = response;
      } else {
        // console.error("Unexpected response format:", response);
        // console.error(
        //   "Response is not an array and has no expected properties"
        // );

        // Try to extract any array from the response
        if (response && typeof response === "object") {
          const possibleArrays = Object.values(response).filter(Array.isArray);
          if (possibleArrays.length > 0) {
            console.log("Found possible array in response:", possibleArrays[0]);
            conditionsData = possibleArrays[0];
          } else {
            throw new Error("Unexpected API response format - no array found");
          }
        } else {
          throw new Error("Unexpected API response format");
        }
      }

      // if (!Array.isArray(conditionsData)) {
      //   throw new Error(`Expected array but got ${typeof conditionsData}`);
      // }

      const transformedConditions = conditionsData.map((condition, index) => {
        try {
          return {
            id: condition._id || condition.id,
            alias: condition.name || condition.alias,
            url: condition.detail || condition.url || condition.details || "",
            status: condition.status || "active",
            details: condition.details || "",
            displayOnCard: condition.displayOnCard,
          };
        } catch (transformError) {
          console.error(
            `Error transforming condition at index ${index}:`,
            condition,
            transformError,
          );
          return {
            id: `error-${index}`,
            alias: "Error loading condition",
            url: "",
            status: "inactive",
            details: "Failed to load condition data",
          };
        }
      });

      setConditions(transformedConditions);

      setError(null); // Clear any previous errors
    } catch (error) {
      console.error("Error loading medical conditions:", error);
      console.error("Error details:", error.message, error.stack);

      if (
        error.message.includes("401") ||
        error.message.includes("Unauthorized")
      ) {
        setError("Session expired. Please log in again.");
      } else if (
        error.message.includes("Network") ||
        error.message.includes("fetch")
      ) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(`Failed to load medical conditions: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load therapies from API
  const loadTherapies = async () => {
    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token found for therapies");
      return;
    }

    try {
      const response = await getTherapies(token);

      // Transform API response to match local state structure
      let therapiesData = [];

      if (
        response &&
        response.data &&
        response.data.therapies &&
        Array.isArray(response.data.therapies)
      ) {
        therapiesData = response.data.therapies;
      } else if (response && response.data && Array.isArray(response.data)) {
        therapiesData = response.data;
      } else if (
        response &&
        response.therapies &&
        Array.isArray(response.therapies)
      ) {
        therapiesData = response.therapies;
      } else if (response && Array.isArray(response)) {
        therapiesData = response;
      } else {
        console.error("Unexpected therapy response format:", response);
        return;
      }

      if (!Array.isArray(therapiesData)) {
        console.error("Expected array but got", typeof therapiesData);
        return;
      }

      const transformedTherapies = therapiesData.map((therapy, index) => {
        try {
          return {
            id: therapy._id || therapy.id,
            name: therapy.name || "",
            description: therapy.description || "",
            frequency: therapy.frequency || "",
            duration: therapy.duration || "",
            status: therapy.status || "active",
          };
        } catch (transformError) {
          console.error(
            `Error transforming therapy at index ${index}:`,
            therapy,
            transformError,
          );
          return {
            id: `error-${index}`,
            name: "Error loading therapy",
            description: "",
            frequency: "",
            duration: "",
            status: "inactive",
          };
        }
      });

      setTherapies(transformedTherapies);
    } catch (error) {
      console.error("Error loading therapies:", error);
      if (
        error.message.includes("401") ||
        error.message.includes("Unauthorized")
      ) {
        setError("Session expired. Please log in again.");
      } else {
        setError("Failed to load therapies. Please try again.");
      }
    }
  };

  const loadMedicalDevices = async () => {
    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token found");
      setError("No authentication token found. Please log in again.");
      return;
    }

    try {
      const response = await getMedicalDevices(token);

      // Handle different possible response structures
      let devicesData = [];
      if (response.data && Array.isArray(response.data.medicalDevices)) {
        devicesData = response.data.medicalDevices;
      } else if (response.data && Array.isArray(response.data)) {
        devicesData = response.data;
      } else if (
        response.medicalDevices &&
        Array.isArray(response.medicalDevices)
      ) {
        devicesData = response.medicalDevices;
      } else if (Array.isArray(response)) {
        devicesData = response;
      } else {
        console.warn(
          "Unexpected medical devices response structure:",
          response,
        );
        devicesData = [];
      }

      // Transform API data to local state format
      const transformedDevices = devicesData.map((device, index) => {
        try {
          return {
            id: device._id || device.id || `device-${index}`,
            name: device.deviceName || device.name || "",
            type: device.deviceType || device.type || "",
            brand: device.brand || "",
            model: device.model || "",
            description: device.description || "",
            status: device.status || "active",
          };
        } catch (transformError) {
          console.error(
            `Error transforming device at index ${index}:`,
            device,
            transformError,
          );
          return {
            id: `error-${index}`,
            name: "Error loading device",
            type: "",
            brand: "",
            model: "",
            description: "",
            status: "inactive",
          };
        }
      });

      setMedicalDevices(transformedDevices);
    } catch (error) {
      console.error("Error loading medical devices:", error);
      if (
        error.message.includes("401") ||
        error.message.includes("Unauthorized")
      ) {
        setError("Session expired. Please log in again.");
      } else {
        setError("Failed to load medical devices. Please try again.");
      }
    }
  };

  // Handle clicks outside condition dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        conditionDropdownRef.current &&
        !conditionDropdownRef.current.contains(event.target)
      ) {
        setShowConditionDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch medical conditions from NLM API
  useEffect(() => {
    const fetchConditions = async () => {
      if (!conditionSearch || conditionSearch.length < 2) {
        setConditionResults([]);
        setIsConditionLoading(false);
        return;
      }

      setIsConditionLoading(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/conditions/v3/search?terms=${encodeURIComponent(
          conditionSearch,
        )}&count=5&df=consumer_name,primary_name&ef=icd10cm_codes,term_icd9_code,term_icd9_text`;
        const res = await fetch(url);
        const data = await res.json();

        // Parse the API response according to the documentation
        // data[3] contains the display fields (consumer_name, primary_name)
        // data[2] contains the extra fields (icd10cm_codes, term_icd9_code, term_icd9_text)
        const results = (data[3] || []).map((row, idx) => ({
          consumerName: row[0] || "",
          primaryName: row[1] || "",
          icd10cmCodes: data[2]?.icd10cm_codes?.[idx] || "",
          icd9Code: data[2]?.term_icd9_code?.[idx] || "",
          icd9Text: data[2]?.term_icd9_text?.[idx] || "",
        }));

        setConditionResults(results);
        setShowConditionDropdown(true);
      } catch (e) {
        console.error("Error fetching conditions:", e);
        setConditionResults([]);
        setShowConditionDropdown(true);
      } finally {
        setIsConditionLoading(false);
      }
    };
    const timeout = setTimeout(fetchConditions, 400);
    return () => clearTimeout(timeout);
  }, [conditionSearch]);

  const statusOptions = [
    { value: "all", label: t("allStatuses", lang) },
    { value: "active", label: t("active", lang) },
    { value: "inactive", label: t("inactive", lang) },
  ];

  // Handlers
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleStatusChange = (e) => setStatusFilter(e.target.value);

  const handleConditionSearchChange = (e) => {
    setConditionSearch(e.target.value);
    if (e.target.value.length >= 2) {
      setShowConditionDropdown(true);
    } else {
      setShowConditionDropdown(false);
    }
  };

  const handleConditionSelect = async (condition) => {
    const token = getAuthToken();
    if (!token) {
      setError("No authentication token found");
      return;
    }

    const conditionName = condition.consumerName || condition.primaryName;
    const conditionData = {
      name: conditionName,
      detail:
        `${condition.icd9Code ? `ICD-9: ${condition.icd9Code}` : ""}${
          condition.icd10cmCodes ? ` ICD-10: ${condition.icd10cmCodes}` : ""
        }`.trim() || "Medical condition from search",
      status: "active",
    };

    setIsSaving(true);
    setError(null);
    try {
      const response = await addMedicalCondition(conditionData, token);

      // Add to local state
      const newCondition = {
        id: response.data.conditionId || "",
        alias: conditionName,
        url: conditionData.detail,
        status: "active",
        details: conditionData.detail,
      };

      setConditions((prev) => [...prev, newCondition]);
      setConditionSearch("");
      setShowConditionDropdown(false);
      setSuccessMessage("Medical condition added successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error adding medical condition:", error);
      setError("Failed to add medical condition. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const addCondition = async () => {
    if (!tempCondition.name.trim()) return;

    const token = getAuthToken();
    if (!token) {
      setError("No authentication token found");
      return;
    }

    const conditionData = {
      name: tempCondition.name.trim(),
      details: tempCondition.details.trim(),
      status: "active",
    };

    setIsSaving(true);
    setError(null);
    try {
      const response = await addMedicalCondition(conditionData, token);

      // Add to local state
      const newCondition = {
        id: response.data.conditionId || "",
        alias: tempCondition.name.trim(),
        url: conditionData.details,
        status: "active",
        details: conditionData.details,
      };

      setConditions((prev) => [...prev, newCondition]);
      setTempCondition({ name: "", details: "" });
      setSuccessMessage("Medical condition added successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error adding medical condition:", error);
      setError("Failed to add medical condition. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = (index) => {
    const condition = conditions[index];

    if (!condition.id) {
      console.error("No condition ID found for deletion");
      return;
    }

    setConditionToDelete({ index, condition });
    setShowDeleteConfirm(true);
  };

  const removeCondition = async () => {
    if (!conditionToDelete) return;

    const { index, condition } = conditionToDelete;
    const token = getAuthToken();
    if (!token) {
      setError("No authentication token found");
      setShowDeleteConfirm(false);
      setConditionToDelete(null);
      return;
    }

    setDeletingIndex(index);
    setError(null);
    try {
      await deleteMedicalCondition(condition.id, token);

      // Remove from local state
      setConditions((prev) => prev.filter((_, i) => i !== index));
      setSuccessMessage("Medical condition deleted successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error deleting medical condition:", error);
      setError("Failed to delete medical condition. Please try again.");
    } finally {
      setDeletingIndex(null);
      setShowDeleteConfirm(false);
      setConditionToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setConditionToDelete(null);
  };

  const startEdit = (index) => {
    const condition = conditions[index];
    setEditingIndex(index);
    setEditForm({
      alias: condition.alias,
      url: condition.url,
      status: condition.status,
      details: condition.details,
    });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditForm({ alias: "", url: "", status: "active", details: "" });
  };

  const saveEdit = async () => {
    if (!editForm.alias.trim()) return;

    const condition = conditions[editingIndex];

    if (!condition.id) {
      throw new Error("Try refreshing the page");
    }

    const token = getAuthToken();
    if (!token) {
      setError("No authentication token found");
      return;
    }

    const conditionData = {
      name: editForm.alias.trim(),
      details: editForm.details.trim(),
      status: editForm.status,
    };

    setIsSaving(true);
    setError(null);
    try {
      await updateMedicalCondition(condition.id, conditionData, token);

      setConditions((prev) =>
        prev.map((condition, index) =>
          index === editingIndex
            ? {
                ...condition,
                alias: editForm.alias.trim(),
                url: editForm.url.trim(),
                status: editForm.status,
                details: editForm.details.trim(),
              }
            : condition,
        ),
      );
      cancelEdit();
      setSuccessMessage("Medical condition updated successfully");

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error updating medical condition:", error);
      setError("Failed to update medical condition. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // Therapy management functions
  const addTherapyLocal = async () => {
    if (!tempTherapy.trim()) return;

    const token = getAuthToken();
    if (!token) {
      setError("No authentication token found");
      return;
    }

    const therapyData = {
      name: tempTherapy.trim(),
      description: "",
      frequency: "",
      duration: "",
      status: "active",
    };

    setIsSavingTherapy(true);
    setError(null);
    try {
      const response = await addTherapy(therapyData, token);

      // Add to local state
      const newTherapy = {
        id:
          response.data?._id ||
          response.data?.id ||
          response._id ||
          response.id,
        name: tempTherapy.trim(),
        description: "",
        frequency: "",
        duration: "",
        status: "active",
      };

      setTherapies((prev) => [...prev, newTherapy]);
      setTempTherapy("");
      setSuccessMessage("Therapy added successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error adding therapy:", error);
      setError("Failed to add therapy. Please try again.");
    } finally {
      setIsSavingTherapy(false);
    }
  };

  const confirmTherapyDelete = (index) => {
    const therapy = therapies[index];
    if (!therapy.id) {
      console.error("No therapy ID found for deletion");
      return;
    }

    setTherapyToDelete({ index, therapy });
    setShowTherapyDeleteConfirm(true);
  };

  const removeTherapy = async () => {
    if (!therapyToDelete) return;

    const { index, therapy } = therapyToDelete;
    const token = getAuthToken();
    if (!token) {
      setError("No authentication token found");
      setShowTherapyDeleteConfirm(false);
      setTherapyToDelete(null);
      return;
    }

    setDeletingTherapyIndex(index);
    setError(null);
    try {
      await deleteTherapy(therapy.id, token);

      // Remove from local state
      setTherapies((prev) => prev.filter((_, i) => i !== index));
      setSuccessMessage("Therapy deleted successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error deleting therapy:", error);
      setError("Failed to delete therapy. Please try again.");
    } finally {
      setDeletingTherapyIndex(null);
      setShowTherapyDeleteConfirm(false);
      setTherapyToDelete(null);
    }
  };

  const cancelTherapyDelete = () => {
    setShowTherapyDeleteConfirm(false);
    setTherapyToDelete(null);
  };

  const startEditTherapy = (index) => {
    const therapy = therapies[index];
    setEditingTherapyIndex(index);
    setEditTherapyForm({
      name: therapy.name,
      description: therapy.description,
      frequency: therapy.frequency,
      duration: therapy.duration,
      status: therapy.status,
    });
  };

  const cancelEditTherapy = () => {
    setEditingTherapyIndex(null);
    setEditTherapyForm({
      name: "",
      description: "",
      frequency: "",
      duration: "",
      status: "active",
    });
  };

  const saveEditTherapy = async () => {
    if (!editTherapyForm.name.trim()) return;

    const therapy = therapies[editingTherapyIndex];

    if (!therapy.id) {
      console.error("No therapy ID found for update");
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError("No authentication token found");
      return;
    }

    const therapyData = {
      name: editTherapyForm.name.trim(),
      description: editTherapyForm.description.trim(),
      frequency: editTherapyForm.frequency.trim(),
      duration: editTherapyForm.duration.trim(),
      status: editTherapyForm.status,
    };

    setIsSavingTherapy(true);
    setError(null);
    try {
      await updateTherapy(therapy.id, therapyData, token);

      setTherapies((prev) =>
        prev.map((therapy, index) =>
          index === editingTherapyIndex
            ? {
                ...therapy,
                name: editTherapyForm.name.trim(),
                description: editTherapyForm.description.trim(),
                frequency: editTherapyForm.frequency.trim(),
                duration: editTherapyForm.duration.trim(),
                status: editTherapyForm.status,
              }
            : therapy,
        ),
      );
      cancelEditTherapy();
      setSuccessMessage("Therapy updated successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error updating therapy:", error);
      setError("Failed to update therapy. Please try again.");
    } finally {
      setIsSavingTherapy(false);
    }
  };

  const handleEditTherapyFormChange = (e) => {
    const { name, value } = e.target;
    setEditTherapyForm((prev) => ({ ...prev, [name]: value }));
  };

  // Medical device management functions
  const addDeviceLocal = async () => {
    if (!tempDevice.trim()) return;

    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token found");
      setError("No authentication token found. Please log in again.");
      return;
    }

    const deviceData = {
      deviceName: tempDevice.trim(),
      deviceType: "",
      brand: "",
      model: "",
      description: "",
      status: "active",
    };

    setIsSavingDevice(true);
    setError(null);
    try {
      const response = await addMedicalDevice(deviceData, token);

      // Add to local state
      const newDevice = {
        id:
          response.data?._id ||
          response.data?.id ||
          response._id ||
          response.id,
        name: tempDevice.trim(),
        type: "",
        brand: "",
        model: "",
        description: "",
        status: "active",
      };

      setMedicalDevices((prev) => [...prev, newDevice]);
      setTempDevice("");
      setSuccessMessage("Medical device added successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error adding device:", error);
      setError("Failed to add medical device. Please try again.");
    } finally {
      setIsSavingDevice(false);
    }
  };

  const confirmDeviceDelete = (index) => {
    const device = medicalDevices[index];
    if (!device.id) {
      // If no ID, it's a local-only device, delete immediately
      removeDevice(index);
      return;
    }
    setDeviceToDelete({ index, device });
    setShowDeviceDeleteConfirm(true);
  };

  const removeDevice = async (index) => {
    const device = medicalDevices[index];
    if (!device.id) {
      // If no ID, it's a local-only device, delete from state only
      setMedicalDevices((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token found");
      setError("No authentication token found. Please log in again.");
      return;
    }

    setDeletingDeviceIndex(index);
    setError(null);
    try {
      await deleteMedicalDevice(device.id, token);
      setMedicalDevices((prev) => prev.filter((_, i) => i !== index));
      setSuccessMessage("Medical device deleted successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error deleting device:", error);
      setError("Failed to delete medical device. Please try again.");
    } finally {
      setDeletingDeviceIndex(null);
    }
  };

  const cancelDeviceDelete = () => {
    setShowDeviceDeleteConfirm(false);
    setDeviceToDelete(null);
  };

  const startEditDevice = (index) => {
    const device = medicalDevices[index];
    setEditingDeviceIndex(index);
    setEditDeviceForm({
      name: device.name,
      type: device.type,
      brand: device.brand,
      model: device.model,
      description: device.description,
      status: device.status,
    });
  };

  const cancelEditDevice = () => {
    setEditingDeviceIndex(null);
    setEditDeviceForm({
      name: "",
      type: "",
      brand: "",
      model: "",
      description: "",
      status: "active",
    });
  };

  const handleSaveAndNext = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      if (tempCondition.name && tempCondition.name.trim()) {
        const conditionData = {
          name: tempCondition.name.trim(),
          detail: tempCondition.details.trim(),
          status: "active",
        };
        await addMedicalCondition(conditionData, token);
      }

      if (tempTherapy.trim()) {
        const therapyData = {
          name: tempTherapy.trim(),
          description: "",
          frequency: "",
          duration: "",
          status: "active",
        };
        await addTherapy(therapyData, token);
      }

      if (tempDevice.trim()) {
        const deviceData = {
          deviceName: tempDevice.trim(),
          deviceType: "",
          brand: "",
          model: "",
          description: "",
          status: "active",
        };
        await addMedicalDevice(deviceData, token);
      }

      setTempCondition({ name: "", details: "" });
      setTempTherapy("");
      setTempDevice("");

      setSuccessMessage("All changes saved successfully!");
    } catch (error) {
      console.error("Error saving data:", error);
      setError(error.message || "Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const saveEditDevice = async () => {
    if (!editDeviceForm.name.trim()) return;

    const device = medicalDevices[editingDeviceIndex];
    if (!device.id) {
      // If no ID, it's a local-only device, update state only
      setMedicalDevices((prev) =>
        prev.map((device, index) =>
          index === editingDeviceIndex
            ? {
                ...device,
                name: editDeviceForm.name.trim(),
                type: editDeviceForm.type.trim(),
                brand: editDeviceForm.brand.trim(),
                model: editDeviceForm.model.trim(),
                description: editDeviceForm.description.trim(),
                status: editDeviceForm.status,
              }
            : device,
        ),
      );
      cancelEditDevice();
      return;
    }

    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token found");
      setError("No authentication token found. Please log in again.");
      return;
    }

    const deviceData = {
      deviceName: editDeviceForm.name.trim(),
      deviceType: editDeviceForm.type.trim(),
      brand: editDeviceForm.brand.trim(),
      model: editDeviceForm.model.trim(),
      description: editDeviceForm.description.trim(),
      status: editDeviceForm.status,
    };

    setIsSavingDevice(true);
    setError(null);
    try {
      await updateMedicalDevice(device.id, deviceData, token);

      setMedicalDevices((prev) =>
        prev.map((device, index) =>
          index === editingDeviceIndex
            ? {
                ...device,
                name: editDeviceForm.name.trim(),
                type: editDeviceForm.type.trim(),
                brand: editDeviceForm.brand.trim(),
                model: editDeviceForm.model.trim(),
                description: editDeviceForm.description.trim(),
                status: editDeviceForm.status,
              }
            : device,
        ),
      );
      cancelEditDevice();
      setSuccessMessage("Medical device updated successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error updating device:", error);
      setError("Failed to update medical device. Please try again.");
    } finally {
      setIsSavingDevice(false);
    }
  };

  const handleEditDeviceFormChange = (e) => {
    const { name, value } = e.target;
    setEditDeviceForm((prev) => ({ ...prev, [name]: value }));
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center p-2">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
      <span className="ml-2 text-xs text-[#837f90]">Searching...</span>
    </div>
  );

  // No results component
  const NoResultsMessage = ({ searchTerm }) => (
    <div className="p-3 text-center">
      <div className="text-xs text-[#837f90] mb-1">
        {t("noConditionsFoundSearch", lang).replace("{searchTerm}", searchTerm)}
      </div>
      <div className="text-xs text-[#837f90] opacity-75">
        {t("tryDifferentSearch", lang)}
      </div>
    </div>
  );

  // Filtered conditions
  const filteredConditions = conditions.filter((cond) => {
    const matchesSearch = cond.alias
      ?.toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || cond.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const showInfoColumn = filteredConditions.some(
    (condition) => condition.displayOnCard,
  );

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-4xl">
        {/* Error and Success Messages */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mt-4">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 mt-4">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-8 mt-4">
          {/* Medical Conditions */}
          <AccountSection>{t("medicalConditions", lang)}</AccountSection>
          <div className="space-y-4 mb-6">
            {/* Manual condition entry */}
            <div className="flex items-start gap-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-start w-full">
                <div className="w-full">
                  <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                    {t("Condition Name", lang)}
                  </label>
                  <input
                    value={tempCondition.name}
                    onChange={(e) =>
                      setTempCondition((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder={
                      t("Condition Name", lang) || "Add condition manually..."
                    }
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                  />
                </div>
                <div className="w-full">
                  <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                    {t("Condition Details", lang)}
                  </label>
                  <textarea
                    value={tempCondition.details}
                    onChange={(e) =>
                      setTempCondition((prev) => ({
                        ...prev,
                        details: e.target.value,
                      }))
                    }
                    placeholder={
                      t("Condition Details", lang) || "Add condition details..."
                    }
                    className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                    rows={7}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  addCondition();
                }}
                disabled={
                  isSaving ||
                  !tempCondition.name.trim() ||
                  !tempCondition.details.trim()
                }
                className="px-4 py-2 mt-5 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[38px]"
              >
                {isSaving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  "+"
                )}
              </button>
            </div>

            {/* Search conditions */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("searchConditions", lang)}
              </label>
              <div className="relative" ref={conditionDropdownRef}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={conditionSearch}
                    onChange={handleConditionSearchChange}
                    placeholder={t("searchConditionsPlaceholder", lang)}
                    className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                  />
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded bg-[#f7f8fa] border border-gray-200">
                    {isConditionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#4962d8]"></div>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        fill="none"
                        stroke="#837f90"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                    )}
                  </span>
                </div>
                {showConditionDropdown && (
                  <div className="absolute left-0 top-12 w-full bg-white border border-gray-200 rounded shadow-lg z-20 max-h-64 overflow-y-auto">
                    {isConditionLoading ? (
                      <LoadingSpinner />
                    ) : conditionResults.length > 0 ? (
                      conditionResults.map((condition, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-[#f7f8fa] border-b border-gray-100 last:border-b-0"
                          onClick={() => handleConditionSelect(condition)}
                        >
                          <div className="font-semibold text-[#2d348f]">
                            {condition.consumerName || condition.primaryName}
                          </div>
                          <div className="text-xs text-[#837f90]">
                            {condition.icd9Code &&
                              `ICD-9: ${condition.icd9Code}`}
                            {condition.icd10cmCodes &&
                              condition.icd9Code &&
                              " • "}
                            {condition.icd10cmCodes &&
                              `ICD-10: ${condition.icd10cmCodes}`}
                          </div>
                          {condition.icd9Text && (
                            <div className="text-xs text-[#837f90] italic">
                              {condition.icd9Text}
                            </div>
                          )}
                        </button>
                      ))
                    ) : conditionSearch.length >= 2 ? (
                      <NoResultsMessage searchTerm={conditionSearch} />
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Therapy/Treatment Section */}
          <AccountSection>
            {t("therapyTreatment", lang) || "Therapy & Treatment"}
          </AccountSection>
          <div className="space-y-4 mb-6">
            {/* Manual therapy entry */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("addTherapy", lang) || "Add Therapy/Treatment"}
              </label>
              <div className="flex gap-2">
                <input
                  value={tempTherapy}
                  onChange={(e) => setTempTherapy(e.target.value)}
                  placeholder={
                    t("addTherapy", lang) || "Add therapy/treatment manually..."
                  }
                  className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                  onKeyDown={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addTherapyLocal())
                  }
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    addTherapyLocal();
                  }}
                  disabled={isSavingTherapy || !tempTherapy.trim()}
                  className="px-4 py-2 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingTherapy ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    "+"
                  )}
                </button>
              </div>
            </div>

            {/* Display added therapies */}
            {therapies.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                  {t("myTherapies", lang) || "My Therapies"}
                </label>
                {therapies.map((therapy, index) => {
                  const isEditingTherapy = editingTherapyIndex === index;

                  return (
                    <div
                      key={index}
                      className="bg-[#f7f8fa] p-3 rounded text-sm border"
                    >
                      {isEditingTherapy ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("tableName", lang)}
                              </label>
                              <input
                                type="text"
                                name="name"
                                value={editTherapyForm.name}
                                onChange={handleEditTherapyFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("status", lang)}
                              </label>
                              <select
                                name="status"
                                value={editTherapyForm.status}
                                onChange={handleEditTherapyFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              >
                                <option value="active">
                                  {t("active", lang)}
                                </option>
                                <option value="inactive">
                                  {t("inactive", lang)}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                              {t("description", lang)}
                            </label>
                            <input
                              type="text"
                              name="description"
                              value={editTherapyForm.description}
                              onChange={handleEditTherapyFormChange}
                              className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("frequency", lang)}
                              </label>
                              <input
                                type="text"
                                name="frequency"
                                value={editTherapyForm.frequency}
                                onChange={handleEditTherapyFormChange}
                                placeholder="e.g., 3 times per week"
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("duration", lang)}
                              </label>
                              <input
                                type="text"
                                name="duration"
                                value={editTherapyForm.duration}
                                onChange={handleEditTherapyFormChange}
                                placeholder="e.g., 6 weeks"
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-2">
                            <button
                              type="button"
                              onClick={saveEditTherapy}
                              disabled={isSavingTherapy}
                              className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t("save", lang)}
                            >
                              {isSavingTherapy ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                              ) : (
                                <svg
                                  width="16"
                                  height="16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                >
                                  <polyline points="9,11 12,14 22,4" />
                                  <path d="M21,12v7a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V5a2,2,0,0,1,2-2h11" />
                                </svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditTherapy}
                              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-50"
                              title={t("cancel", lang)}
                            >
                              <svg
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-[#2d348f]">
                              {therapy.name}
                            </div>
                            {therapy.description && (
                              <div className="text-xs text-[#837f90] mt-1">
                                {therapy.description}
                              </div>
                            )}
                            <div className="flex gap-4 text-xs text-[#837f90] mt-1">
                              {therapy.frequency && (
                                <span>
                                  {t("frequency", lang)}: {therapy.frequency}
                                </span>
                              )}
                              {therapy.duration && (
                                <span>
                                  {t("duration", lang)}: {therapy.duration}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block px-2 py-1 text-xs rounded-full ${
                                therapy.status === "active"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {t(therapy.status, lang)}
                            </span>
                            <button
                              type="button"
                              onClick={() => startEditTherapy(index)}
                              disabled={
                                isSavingTherapy ||
                                deletingTherapyIndex === index
                              }
                              className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t("edit", lang)}
                            >
                              <svg
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => confirmTherapyDelete(index)}
                              disabled={deletingTherapyIndex === index}
                              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t("delete", lang)}
                            >
                              {deletingTherapyIndex === index ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                              ) : (
                                <svg
                                  width="16"
                                  height="16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                >
                                  <polyline points="3,6 5,6 21,6" />
                                  <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Medical Devices Section */}
          <AccountSection>
            {t("medicalDevices", lang) || "Medical Devices"}
          </AccountSection>
          <div className="space-y-4 mb-6">
            {/* Manual device entry */}
            <div>
              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                {t("addDevice", lang) || "Add Medical Device"}
              </label>
              <div className="flex gap-2">
                <input
                  value={tempDevice}
                  onChange={(e) => setTempDevice(e.target.value)}
                  placeholder={
                    t("addDevice", lang) || "Add medical device manually..."
                  }
                  className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                  onKeyDown={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addDeviceLocal())
                  }
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    addDeviceLocal();
                  }}
                  disabled={isSavingDevice || !tempDevice.trim()}
                  className="px-4 py-2 bg-[#4962d8] text-white rounded text-sm hover:bg-[#2d348f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingDevice ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    "+"
                  )}
                </button>
              </div>
            </div>

            {/* Display added devices */}
            {medicalDevices.length > 0 && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                  {t("myDevices", lang) || "My Medical Devices"}
                </label>
                {medicalDevices.map((device, index) => {
                  const isEditingDevice = editingDeviceIndex === index;

                  return (
                    <div
                      key={index}
                      className="bg-[#f7f8fa] p-3 rounded text-sm border"
                    >
                      {isEditingDevice ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("deviceName", lang) || "Device Name"}
                              </label>
                              <input
                                type="text"
                                name="name"
                                value={editDeviceForm.name}
                                onChange={handleEditDeviceFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("deviceType", lang) || "Device Type"}
                              </label>
                              <input
                                type="text"
                                name="type"
                                value={editDeviceForm.type}
                                onChange={handleEditDeviceFormChange}
                                placeholder="e.g., Monitoring Device, Implant, etc."
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("brand", lang) || "Brand"}
                              </label>
                              <input
                                type="text"
                                name="brand"
                                value={editDeviceForm.brand}
                                onChange={handleEditDeviceFormChange}
                                placeholder="e.g., Omron, Medtronic, etc."
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("model", lang) || "Model"}
                              </label>
                              <input
                                type="text"
                                name="model"
                                value={editDeviceForm.model}
                                onChange={handleEditDeviceFormChange}
                                placeholder="e.g., BP742N"
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("description", lang)}
                              </label>
                              <input
                                type="text"
                                name="description"
                                value={editDeviceForm.description}
                                onChange={handleEditDeviceFormChange}
                                placeholder="Usage description"
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-[#837f90] mb-1 block">
                                {t("status", lang)}
                              </label>
                              <select
                                name="status"
                                value={editDeviceForm.status}
                                onChange={handleEditDeviceFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              >
                                <option value="active">
                                  {t("active", lang)}
                                </option>
                                <option value="inactive">
                                  {t("inactive", lang)}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 pt-2">
                            <button
                              type="button"
                              onClick={saveEditDevice}
                              disabled={isSavingDevice}
                              className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t("save", lang)}
                            >
                              {isSavingDevice ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                              ) : (
                                <svg
                                  width="16"
                                  height="16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                >
                                  <polyline points="9,11 12,14 22,4" />
                                  <path d="M21,12v7a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V5a2,2,0,0,1,2-2h11" />
                                </svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditDevice}
                              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-50"
                              title={t("cancel", lang)}
                            >
                              <svg
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-[#2d348f]">
                              {device.name}
                            </div>
                            {device.description && (
                              <div className="text-xs text-[#837f90] mt-1">
                                {device.description}
                              </div>
                            )}
                            <div className="flex gap-4 text-xs text-[#837f90] mt-1">
                              {device.type && (
                                <span>
                                  {t("deviceType", lang) || "Type"}:{" "}
                                  {device.type}
                                </span>
                              )}
                              {device.brand && (
                                <span>
                                  {t("brand", lang) || "Brand"}: {device.brand}
                                </span>
                              )}
                              {device.model && (
                                <span>
                                  {t("model", lang) || "Model"}: {device.model}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block px-2 py-1 text-xs rounded-full ${
                                device.status === "active"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {t(device.status, lang)}
                            </span>
                            <button
                              type="button"
                              onClick={() => startEditDevice(index)}
                              disabled={
                                isSavingDevice || deletingDeviceIndex === index
                              }
                              className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t("edit", lang)}
                            >
                              <svg
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => confirmDeviceDelete(index)}
                              disabled={
                                isSavingDevice || deletingDeviceIndex === index
                              }
                              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={t("delete", lang)}
                            >
                              {deletingDeviceIndex === index ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                              ) : (
                                <svg
                                  width="16"
                                  height="16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                >
                                  <polyline points="3,6 5,6 21,6" />
                                  <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1-2-2h4a2,2,0,0,1,2,2V6" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving || isSavingTherapy || isSavingDevice}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> {t("back", lang)}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving || isSavingTherapy || isSavingDevice}
              onClick={handleSaveAndNext}
            >
              <span className="mr-2">{t("save", lang)}</span>
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving || isSavingTherapy || isSavingDevice}
            >
              <>
                {t("next", lang)} <span className="ml-2">&raquo;</span>
              </>
            </AccountButton>
          </div>

          {/* My Conditions Table */}
          <AccountSection>{t("myConditions", lang)}</AccountSection>
          <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
            <div className="flex-1 flex items-center gap-2">
              <input
                id="condition-search"
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={t("searchPlaceholder", lang)}
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                style={{ maxWidth: 200 }}
              />
              <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-[#f7f8fa] border border-gray-200">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="#837f90"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
            </div>
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
              style={{ maxWidth: 140 }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto max-w-xs sm:max-w-full">
            <table className="min-w-full bg-white text-sm">
              <thead>
                <tr className="bg-[#f7f8fa] text-[#837f90]">
                  {showInfoColumn && (
                    <th className="px-4 py-2 text-left font-semibold">Info</th>
                  )}
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("tableName", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("tableDetails", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("status", lang)}
                  </th>
                  <th className="px-4 py-2 text-left font-semibold">
                    {t("actions", lang)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-[#837f90]">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#4962d8] mr-2"></div>
                        Loading conditions...
                      </div>
                    </td>
                  </tr>
                ) : filteredConditions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-4 text-[#837f90]">
                      {t("noConditionsFound", lang)}
                    </td>
                  </tr>
                ) : (
                  filteredConditions.map((cond, idx) => {
                    const actualIndex = conditions.findIndex((c) => c === cond);
                    const isEditing = editingIndex === actualIndex;
                    console.log("Edit form details", editForm.details);

                    return (
                      <tr key={idx} className="border-t border-gray-100">
                        {isEditing ? (
                          <>
                            {showInfoColumn && (
                              <td className="px-4 py-2">
                                {cond.displayOnCard && <DisplayOnCardPill />}
                              </td>
                            )}
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                name="alias"
                                value={editForm.alias}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                name="details"
                                value={editForm.details}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <select
                                name="status"
                                value={editForm.status}
                                onChange={handleEditFormChange}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#4962d8]"
                              >
                                <option value="active">
                                  {t("active", lang)}
                                </option>
                                <option value="inactive">
                                  {t("inactive", lang)}
                                </option>
                              </select>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={saveEdit}
                                  disabled={isSaving}
                                  className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={t("save", lang)}
                                >
                                  {isSaving ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                                  ) : (
                                    <svg
                                      width="16"
                                      height="16"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      viewBox="0 0 24 24"
                                    >
                                      <polyline points="9,11 12,14 22,4" />
                                      <path d="M21,12v7a2,2,0,0,1-2,2H5a2,2,0,0,1-2-2V5a2,2,0,0,1,2-2h11" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-50"
                                  title={t("cancel", lang)}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            {showInfoColumn && (
                              <td className="px-4 py-2">
                                {cond.displayOnCard && <DisplayOnCardPill />}
                              </td>
                            )}
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(
                                cond.alias,
                                "No Name",
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {replaceNullWithPlaceholder(
                                cond.details,
                                "No Details",
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-block px-3 py-1 text-xs rounded-full ${
                                  cond.status === "active"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {t(cond.status, lang)}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => startEdit(actualIndex)}
                                  disabled={
                                    isSaving || deletingIndex === actualIndex
                                  }
                                  className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={t("edit", lang)}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => confirmDelete(actualIndex)}
                                  disabled={deletingIndex === actualIndex}
                                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={t("delete", lang)}
                                >
                                  {deletingIndex === actualIndex ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                                  ) : (
                                    <svg
                                      width="16"
                                      height="16"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      viewBox="0 0 24 24"
                                    >
                                      <polyline points="3,6 5,6 21,6" />
                                      <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6" />
                                      <line x1="10" y1="11" x2="10" y2="17" />
                                      <line x1="14" y1="11" x2="14" y2="17" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && conditionToDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDelete}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Medical Condition
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "
                <span className="font-medium">
                  {conditionToDelete.condition.alias}
                </span>
                "? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={removeCondition}
                  disabled={deletingIndex === conditionToDelete.index}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingIndex === conditionToDelete.index ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </div>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Therapy Delete Confirmation Modal */}
      {showTherapyDeleteConfirm && therapyToDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelTherapyDelete}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Therapy
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "
                <span className="font-medium">
                  {therapyToDelete.therapy.name}
                </span>
                "? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={cancelTherapyDelete}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={removeTherapy}
                  disabled={deletingTherapyIndex === therapyToDelete.index}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingTherapyIndex === therapyToDelete.index ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </div>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Delete Confirmation Modal */}
      {showDeviceDeleteConfirm && deviceToDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelDeviceDelete}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Medical Device
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "
                <span className="font-medium">
                  {deviceToDelete.device.name}
                </span>
                "? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={cancelDeviceDelete}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => removeDevice(deviceToDelete.index)}
                  disabled={deletingDeviceIndex === deviceToDelete.index}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingDeviceIndex === deviceToDelete.index ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </div>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
